import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';

import {
  ChurnFlowAnalyticsBaseService,
  ChurnFlowDataBaseService,
  ChurnFlowNavigationBaseService,
} from '@core/churn-flow-logic';
import { ChurnFlowSolutionIconVariant } from '@core/churn-flow-types';
import {
  ChurnFlowSolutionIconComponent,
  ChurnFlowStepTemplateComponent,
} from '@core/churn-flow-ui';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { IntercomService } from '@core/intercom-integration-legacy';
import { ButtonComponent, HeadingComponent, RtButtonVariant } from '@core/user-interface-legacy';

import { ChurnFlowInsightsComponent } from '../../components/churn-flow-insights/churn-flow-insights.component';

interface ChurnOption {
  iconVariant: ChurnFlowSolutionIconVariant;
  heading: string;
  subheading: string;
  buttonVariant: RtButtonVariant;
  buttonText: string;
  action: Function;
}

@Component({
  selector: 'tlc-churn-intro-page',
  templateUrl: './churn-intro-page.component.html',
  styleUrls: ['./churn-intro-page.component.less'],
  imports: [
    ChurnFlowStepTemplateComponent,
    HeadingComponent,
    TranslatePipe,
    InfoboxComponent,
    NgTemplateOutlet,
    ButtonComponent,
    ChurnFlowInsightsComponent,
    ChurnFlowSolutionIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [],
})
export class ChurnIntroPageComponent {
  private readonly t = injectTranslate();
  private readonly router = inject(Router);
  private readonly intercom = inject(IntercomService);
  private readonly analytics = inject(ChurnFlowAnalyticsBaseService);
  private readonly navigationService = inject(ChurnFlowNavigationBaseService);
  private readonly churnFlowDataService = inject(ChurnFlowDataBaseService);

  protected readonly cancellationDatesSignal =
    this.churnFlowDataService.subscriptionCancellationDates;

  protected readonly churnOptions: ChurnOption[] = [
    {
      iconVariant: 'dialog',
      heading: this.t('tlc.churn_flow.steps.intro.contact_support.heading'),
      subheading: this.t('tlc.churn_flow.steps.intro.contact_support.subheading'),
      buttonVariant: 'primary',
      buttonText: this.t('tlc.churn_flow.steps.intro.contact_support.cta_button'),
      action: () => this.contactSupport(),
    },
    {
      iconVariant: 'assignment',
      heading: this.t('tlc.churn_flow.steps.intro.modify_plan.heading'),
      subheading: this.t('tlc.churn_flow.steps.intro.modify_plan.subheading'),
      buttonVariant: 'outline',
      buttonText: this.t('tlc.churn_flow.steps.intro.modify_plan.cta_button'),
      action: () => this.modifyPlan(),
    },
    {
      iconVariant: 'block',
      heading: this.t('tlc.churn_flow.steps.intro.cancel_subscription.heading'),
      subheading: this.t('tlc.churn_flow.steps.intro.cancel_subscription.subheading'),
      buttonVariant: 'outline',
      buttonText: this.t('tlc.churn_flow.steps.intro.cancel_subscription.cta_button'),
      action: () => this.cancelSubscription(),
    },
  ];

  public readonly infoboxText = computed(() => {
    const cancellationDates = this.cancellationDatesSignal();
    const dates = {
      endDate: cancellationDates?.endDate,
      latestCancellationDate: cancellationDates?.latestCancellationDate,
    };

    return this.t('tlc.churn_flow.steps.intro.renew_once_infobox_text', dates);
  });

  public contactSupport(): void {
    this.analytics.trackStepOneContactSupport();
    this.openIntercom(this.t('tlc.churn_flow.steps.intro.contact_support.intercom_message'));
  }

  public modifyPlan(): void {
    this.analytics.trackStepOneModifyMyPlan();
    this.navigationService.navigateToPlanPicker();
  }

  public cancelSubscription(): void {
    this.analytics.trackStepOneContinue();
    this.router.navigate([{ outlets: { dialog: ['churn', 'reasons'] } }]);
  }

  public stayWithTellent(): void {
    this.analytics.trackStayWithTellent({ step: 'step-1' });
    this.navigationService.stayWithTellent();
  }

  private openIntercom(message: string): void {
    this.intercom.startNewMessage(message);
  }
}
